import { c as _c } from "react/compiler-runtime";
import { cva } from 'class-variance-authority';
import type { ClassNameValue } from 'tailwind-merge';
import Icon from '../Icon';
import { cn } from '~/utils';
import type Button from './Button';
type ButtonProps = React.ComponentProps<typeof Button>;

/** @TODO: refactor enums to unions so that they don't need to be imported everywhere */
export const Variant = {
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
  OUTLINE: 'outline',
  OUTLINE_TO_SECONDARY: 'outline-to-secondary',
  LINK: 'link',
  OPAQUE: 'opaque',
  GHOST: 'ghost'
} as const;

/** @TODO: refactor enums to unions so that they don't need to be imported everywhere */
export const Size = {
  XS: 'xs',
  SM: 'sm',
  MD: 'md',
  LG: 'lg',
  XL: 'xl',
  '2XL': '2xl'
} as const;
export type SizeValue = keyof typeof Size;

/** @TODO: refactor enums to unions so that they don't need to be imported everywhere */
export const Grouped = {
  NONE: 'none',
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right'
} as const;
export const buttonVariant = cva(['inline-flex cursor-pointer items-center justify-center font-bold outline-offset-4 transition-all select-none', 'disabled:cursor-not-allowed'], {
  variants: {
    size: {
      [Size.XS]: 'gap-1.5 text-sm active:scale-[97%]',
      // Discuss this: we want to be able to use the button without default padding ...
      [Size.SM]: 'gap-1 rounded-3xl px-3 py-2 text-sm active:scale-[97%]',
      [Size.MD]: 'gap-1.5 rounded-3xl px-4 py-3 text-sm active:scale-[98%]',
      [Size.LG]: 'gap-2 rounded-4xl px-6 py-4 text-base active:scale-[98%]',
      [Size.XL]: 'gap-3 rounded-4xl px-8 py-5 text-base active:scale-[99%]',
      [Size['2XL']]: 'gap-3 rounded-4xl px-8 py-5 text-base active:scale-[99%]'
    },
    variant: {
      [Variant.SECONDARY]: cn('shadow-gray-900/0.5 shadow-2xs border border-secondary bg-secondary text-white', 'hover:bg-secondary-700', 'focus:ring-2 focus:ring-secondary', 'disabled:border-secondary-200 disabled:bg-secondary-200 disabled:opacity-60', 'disabled:hover:bg-secondary-200'),
      [Variant.TERTIARY]: cn('shadow-gray-900/0.5 shadow-2xs bg-tertiary text-white', 'hover:bg-tertiary-700', 'disabled:opacity-60 disabled:hover:bg-tertiary'),
      [Variant.OPAQUE]: cn('shadow-gray-900/0.5 shadow-2xs bg-white/25 text-white', 'hover:bg-white/40', 'disabled:opacity-60 disabled:hover:bg-white/25'),
      [Variant.GHOST]: cn('bg-white/0 text-blue-600', 'hover:bg-white hover:text-blue-800', 'disabled:opacity-60'),
      [Variant.OUTLINE]: cn('shadow-gray-900/0.5 shadow-2xs border border-blue-200 bg-white text-gray-700', 'hover:border-secondary', 'disabled:border-blue-200 disabled:text-gray-300'),
      [Variant.OUTLINE_TO_SECONDARY]: cn('shadow-gray-900/0.5 shadow-2xs border border-blue-200 text-gray-700', 'hover:bg-tertiary-700 hover:text-white group-hover:bg-tertiary-700 group-hover:text-white', 'disabled:text-gray-300'),
      [Variant.LINK]: cn('disabled:text-gray-300')
    },
    grouped: {
      [Grouped.NONE]: '',
      [Grouped.LEFT]: 'rounded-l-4xl',
      [Grouped.CENTER]: '-ml-px',
      [Grouped.RIGHT]: '-ml-px rounded-r-4xl'
    }
  },
  defaultVariants: {
    size: Size.MD,
    variant: Variant.SECONDARY,
    grouped: Grouped.NONE
  }
});
export function ButtonIcon(props) {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.className) {
    t0 = cn("shrink-0", props.className);
    $[0] = props.className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props || $[3] !== t0) {
    t1 = <Icon {...props} className={t0} />;
    $[2] = props;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
export function getIconSize(buttonSize: NonNullable<ButtonProps['size']>) {
  switch (buttonSize) {
    case Size.SM:
      return 'xs';
    case Size.MD:
      return 'sm';
    case Size.LG:
    case Size.XL:
      return 'md';
    default:
      return 'sm';
  }
}
export function getIconClass(variant: NonNullable<ButtonProps['variant']>) {
  switch (variant) {
    case Variant.OUTLINE:
      return 'text-secondary';
    default:
      return '';
  }
}